import * as Sauce from "Sauce";
import * as Validation from "Sauce/Validation";
import * as React from "react";
import { getTextWidth } from "Util";
import { Popover, PopoverTitle, PopoverContent, Button, Tooltip } from "reactstrap";
import * as Util from "Util";
import * as _ from "lodash";
require("./DisplayNumber.scss");
import * as ReactDOMServer from "react-dom/server";

interface PropsBase {
    num: Validation.ParseResult<number>;
    displayRaw: boolean;
    maxWidth?: number;
    minPrecision?: number; /* if this isn't defined, ParseResult won't try to round the number */
    maxPrecision?: number;
    font?: string;
    className?: string;
    style?: React.CSSProperties;
}

export interface TooltipTargetDisplayNumberProps extends PropsBase {
    id: string; /* a dom id for pairing a target with a popover */
    toggle: () => void;
}

export interface TooltipContentProps extends TooltipTargetDisplayNumberProps {
    toggleRaw: () => void;
    open: boolean;
}

interface DisplayNumberState {
    displayRaw: boolean;
    open: boolean;
}

/* Display a ParsedNumber by reformatting it into something that fits within a specified width.
    The final fallback is that the returned component has a tooltip that shows the number.

    Do the first thing that works:
    If displayRaw is true and num.raw fits into maxWidth, display the raw text that the user typed. 
        (This could be a mathjs expression.)
    
    If the formatted value fits into maxWidth, display that.

    If reformatting the number to maxPrecision fits, display that.
    
    If minPrecision is defined and there is some precision ≥ minPrecision that fits, display that.

    Otherwise display no text (but still returning a tooltip target).
*/
const defaultFont = "16px Anonymous Pro";
export function getRenderText(
    num: Validation.ParseResult<number>, 
    maxWidth: number, 
    minPrecision: number, 
    maxPrecision: number, 
    displayRaw: boolean,
    document: HTMLDocument,
    font?: string): string  | undefined 
{
    let renderText: string;
    font = font || defaultFont;
    if (num == null) return;
    if (maxWidth) {
        if (displayRaw) {
            let rawLength = getTextWidth(num.raw, font, document);
            if (rawLength <= maxWidth) {
                renderText = num.raw;
            }
        }
        if (!renderText) {
            let formattedLength = getTextWidth(num.formatted, font, document);
            if (formattedLength <= maxWidth) {
                renderText = num.formatted;
            }
        }
        if (!renderText) {
            let maxPrecisionNum = num.value.toPrecision(maxPrecision);
            let parsedLength = getTextWidth(maxPrecisionNum, font, document);
            if (parsedLength <= maxWidth) {
                renderText = maxPrecisionNum;
            } 
            else if (minPrecision != null){
                /* starting from minPrecision, find the maximum precision that fits, if any */
                let curPrecision = minPrecision || 1;
                let prev: string;
                do {
                    let minPrecisionNum = num.value.toPrecision(curPrecision);
                    if (minPrecisionNum === prev) {
                        break;
                    }
                    parsedLength = getTextWidth(minPrecisionNum, font, document);
                    if (parsedLength <= maxWidth) {
                        renderText = minPrecisionNum;
                    }
                    curPrecision++;
                    prev = minPrecisionNum;
                } while (parsedLength < maxWidth);
            }
        }
    }
    else {
        renderText = displayRaw ? num.raw : num.formatted;
    }
    return renderText || "..";
}

/* plain span, not a tooltip target */
export class DisplayNumberText extends Sauce.Component<PropsBase, {}> {
    constructor(props: TooltipTargetDisplayNumberProps) {
        super(props);
    }
    plainText() {
        const { num, maxWidth, minPrecision, maxPrecision, displayRaw } = this.props;
        const font = this.props.font || defaultFont
        return getRenderText(num, maxWidth, minPrecision, maxPrecision, displayRaw, document, font);
    }
    render() {
        let renderText = this.plainText();

        return <span>{renderText}</span>;
    }
}

/* a tooltip target span */
export class DisplayNumberSpan extends Sauce.Component<TooltipTargetDisplayNumberProps, {}> {
    constructor(props: TooltipTargetDisplayNumberProps) {
        super(props);
    }
    render() {
        const { num, maxWidth, minPrecision, maxPrecision, displayRaw, toggle, id } = this.props;
        const font = this.props.font || defaultFont
        let renderText = getRenderText(num, maxWidth, minPrecision, maxPrecision, displayRaw, document, font);
        return <span 
                onClick={toggle} 
                id={id} 
                style={this.props.style} 
                className={`tappable ${this.props.className}`}>{renderText}</span>
    }
}
/* a tooltip target svg text element */
export class DisplayNumberSvgText extends Sauce.Component<TooltipTargetDisplayNumberProps, {}> {
    render() {
        const { num, maxWidth, minPrecision, maxPrecision, displayRaw, toggle, id } = this.props;
        const font = this.props.font || defaultFont
        let renderText = getRenderText(num, maxWidth, minPrecision, maxPrecision, displayRaw, document, font);
        return <text 
            onClick={toggle} 
            id={id} 
            style={this.props.style} className={`tappable ${this.props.className}`}>{renderText}</text>
    }
}

export class DisplayNumberPopoverContent extends Sauce.Component<TooltipContentProps, {}> {
    render() {
        const { num, maxWidth, id, open, toggle, displayRaw, toggleRaw } = this.props;
 
        return <Popover
            placement="top"
            tether={{ constraints: [{ to: 'scrollParent', pin: true }] }}
            target={id}
            isOpen={open}
            toggle={toggle}>

            <PopoverContent>
                <table className="parsed-number-popover-table"><tbody>
                    <tr>
                        <td>{num.raw}</td>
                        <td>(raw)</td>
                        <td><input
                            onChange={toggleRaw} 
                            type="radio" 
                            value="raw"
                            name="rawgroup" defaultChecked={displayRaw} /></td>
                    </tr>
                    <tr>
                        <td>{num.value}</td>
                        <td>(parsed)</td>
                        <td><input 
                            onChange={toggleRaw} 
                            type="radio" 
                            value="raw"
                            name="rawgroup" defaultChecked={!displayRaw} /></td>
                        </tr></tbody>
                </table>
            </PopoverContent>
        </Popover>;
   }
}
export class DisplayNumberSpanWithPopover extends Sauce.Component<PropsBase, DisplayNumberState> {
    constructor(props: TooltipTargetDisplayNumberProps) {
        super(props);
        this.state = { displayRaw: props.displayRaw, open: false };
    }
    toggleTooltip = () => {
        this.setState({
            open: !this.state.open
        });
    }
    toggleRaw = () => {
        this.setState({
            displayRaw: !this.state.displayRaw
        });
    }

    render(): JSX.Element {
        const { num, maxWidth, minPrecision, maxPrecision } = this.props;
        const { displayRaw, open } = this.state;
        const font = this.props.font || defaultFont

        let id = Util.makeDomId(_.uniqueId());
        if (num == null) return null;
        
        return <div>
                <DisplayNumberSpan 
                    {...this.props}
                    displayRaw={this.state.displayRaw}
                    id={id} 
                    toggle={() => this.setState({ open: !this.state.open })}/>
                <DisplayNumberPopoverContent 
                    {...this.props}
                    id={id}
                    toggle={() => this.setState({ open: !this.state.open })}
                    toggleRaw={() => this.setState({ displayRaw: !this.state.displayRaw })}
                    open={open}/>
            </div>
    }
}


// WEBPACK FOOTER //
// ./src/code/UI/Math/DisplayNumber.tsx